// The theme library's requests, and this console's reading of what came back.
//
// ---------------------------------------------------------------------------
// SESSION WRITES, NO CODE ON THE REQUEST
//
// Every one of these runs on the signed-in session, the way listing clients
// and saving a kiosk playlist do (admin.ts's header has the two kinds). A
// theme is a palette in a library and a pointer on a show; neither changes
// what a stand is showing until that stand's app is published, and the
// publish is the write that takes six digits. So there is no Authorise here
// and nothing in this file holds a code.
//
// NOVA-ONLY FOR NOW, AND THE SERVICE SAYS SO, NOT THIS FILE. api/src/admin/
// routes.ts takes a signed-in nova_admin on every theme route and refuses a
// client viewer and the admin token. Nothing below checks a role: a refusal
// comes back as the service's own sentence through `adminRefusalFor`, which
// is admin.ts's reading and the one every other admin write here uses.
//
// ---------------------------------------------------------------------------
// ONE OUTCOME OF ITS OWN: THE CONTRAST FLOOR
//
// A 422 `theme_below_contrast_floor` is the service measuring the palette with
// the same themeSaveProblems the editor draws, and naming every failing pair.
// It is not "unavailable", which is where admin.ts's reading would file a
// status it has no sentence for, and it is not a fault: it is the editor's own
// refusal arriving from the other end, because a browser is not the only thing
// that can send a theme. So it has its own kind and the page draws the pairs.
//
// ---------------------------------------------------------------------------
// LOGOS GO BACK AS THEY CAME
//
// The update route reads `logos ?? null`, so a PUT that leaves the key out
// clears a theme's marks. The editor here does not edit logos, and a save of
// the colours must not quietly take them off. So every record carries the
// logos it was read with, untouched, and an update sends them straight back.

import { PEOPLE_ENDPOINTS } from "../../../api/src/admin/endpoints.js";
import {
  THEME_ROLE_KEYS,
  type ThemePalette,
  type ThemeRadius,
  type ThemeSurfaceId,
} from "../../../ui/src/themeLibrary.js";
import { adminRefusalFor, type AdminRefusal } from "./admin.js";
import { authenticated, type Answer, type ApiOptions } from "./client.js";
import type { SessionToken } from "../session/store.js";

export type LibraryRecord = {
  readonly themeId: string;
  readonly name: string;
  /** Null is a Nova theme any client may wear. */
  readonly clientId: string | null;
  /** Nova house only: listed, copied, never edited, never pointed at. */
  readonly locked: boolean;
  readonly light: ThemePalette;
  readonly dark: ThemePalette;
  readonly headFont: string;
  readonly bodyFont: string;
  readonly radius: ThemeRadius;
  /** As read, and sent back as read. See the header. */
  readonly logos: Readonly<Record<string, unknown>> | null;
  readonly revisedAt: string | null;
  /** When the theme was archived, or null. The library list never carries an archived one; a theme read on its own may be. */
  readonly archivedAt?: string | null;
};

/** Why an app wears what it wears, in the service's words. */
export type ThemeFollows = "surface" | "own" | "show" | "client" | "nothing";

export type ShowThemes = {
  readonly activationId: string;
  readonly clientId: string;
  /** The event theme. Null follows the client's default. */
  readonly showThemeId: string | null;
  /** A theme id or "own" per app. A missing key follows the show. */
  readonly surfaceThemes: Readonly<Partial<Record<ThemeSurfaceId, string>>>;
  readonly clientDefaultThemeId: string | null;
  readonly surfaces: readonly {
    readonly surface: ThemeSurfaceId;
    readonly themeId: string | null;
    readonly follows: ThemeFollows;
  }[];
};

export type ClientDefault = { readonly clientId: string; readonly themeId: string | null };

export type Archived = {
  readonly theme: LibraryRecord;
  readonly cleared: { readonly clientDefaults: number; readonly showThemes: number; readonly surfacePointers: number };
};

/** The editor's own refusal, sent back by the service. See the header. */
export type BelowFloor = { readonly kind: "below_floor"; readonly detail: string };

export type ThemeAttempt<T> =
  | { readonly ok: true; readonly value: T }
  | ({ readonly ok: false } & (AdminRefusal | BelowFloor));

/**
 * Whether two ids name the same row, without regard to case.
 *
 * The service writes uuid::text in lower case and stores pointers that way,
 * but a route parameter is whatever somebody pasted, and a row saved before
 * #199's lower-casing may carry capitals. Every comparison of a theme, client
 * or pointer id on the theme screens goes through this, because the miss it
 * prevents is silent: a select that matches no option draws the first one,
 * and "wearing this" reads as off.
 */
export function sameId(a: string | null | undefined, b: string | null | undefined): boolean {
  if (a === null || a === undefined || b === null || b === undefined) return false;
  return a.toLowerCase() === b.toLowerCase();
}

export async function listLibrary(
  options: ApiOptions,
  token: SessionToken,
  clientId: string | null,
): Promise<ThemeAttempt<readonly LibraryRecord[]>> {
  const answer = await authenticated(options, token, {
    path:
      clientId === null
        ? PEOPLE_ENDPOINTS.themes
        : `${PEOPLE_ENDPOINTS.themes}?client_id=${encodeURIComponent(clientId)}`,
    method: "GET",
  });
  if (!answer.ok) return refused(answer);
  const raw = fields(answer.body)["themes"];
  if (!Array.isArray(raw)) return unreadable("theme list", "themes");
  const themes: LibraryRecord[] = [];
  for (const entry of raw) {
    const theme = recordFrom(entry);
    if (theme === null) return unreadable("theme list", "every theme's id, name and both palettes");
    themes.push(theme);
  }
  return { ok: true, value: themes };
}

export async function readLibraryTheme(
  options: ApiOptions,
  token: SessionToken,
  themeId: string,
): Promise<ThemeAttempt<LibraryRecord>> {
  const answer = await authenticated(options, token, {
    path: `${PEOPLE_ENDPOINTS.themes}?theme_id=${encodeURIComponent(themeId)}`,
    method: "GET",
  });
  if (!answer.ok) return refused(answer);
  const theme = recordFrom(answer.body);
  return theme === null ? unreadable("theme", "its id, name and both palettes") : { ok: true, value: theme };
}

export type ThemeDraft = {
  readonly name: string;
  readonly light: ThemePalette;
  readonly dark: ThemePalette;
  readonly headFont: string;
  readonly bodyFont: string;
  readonly radius: ThemeRadius;
  readonly logos: Readonly<Record<string, unknown>> | null;
};

/** Create, for `clientId` or for nobody. The client is fixed here and never moved. */
export async function createLibraryTheme(
  options: ApiOptions,
  token: SessionToken,
  input: ThemeDraft & { readonly clientId: string | null },
): Promise<ThemeAttempt<LibraryRecord>> {
  const answer = await authenticated(options, token, {
    path: PEOPLE_ENDPOINTS.themes,
    method: "POST",
    json: { ...wire(input), client_id: input.clientId },
  });
  if (!answer.ok) return refused(answer);
  const theme = recordFrom(answer.body);
  return theme === null ? unreadable("theme", "the theme it saved") : { ok: true, value: theme };
}

export async function updateLibraryTheme(
  options: ApiOptions,
  token: SessionToken,
  input: ThemeDraft & { readonly themeId: string },
): Promise<ThemeAttempt<LibraryRecord>> {
  const answer = await authenticated(options, token, {
    path: PEOPLE_ENDPOINTS.themes,
    method: "PUT",
    json: { ...wire(input), theme_id: input.themeId },
  });
  if (!answer.ok) return refused(answer);
  const theme = recordFrom(answer.body);
  return theme === null ? unreadable("theme", "the theme it saved") : { ok: true, value: theme };
}

export async function archiveLibraryTheme(
  options: ApiOptions,
  token: SessionToken,
  themeId: string,
): Promise<ThemeAttempt<Archived>> {
  const answer = await authenticated(options, token, {
    path: `${PEOPLE_ENDPOINTS.themes}?theme_id=${encodeURIComponent(themeId)}`,
    method: "DELETE",
  });
  if (!answer.ok) return refused(answer);
  const b = fields(answer.body);
  const theme = recordFrom(b["theme"]);
  const cleared = fields(b["cleared"]);
  const counts = [cleared["client_defaults"], cleared["show_themes"], cleared["surface_pointers"]].map(count);
  if (theme === null || counts.some((n) => n === null)) return unreadable("archive", "the theme and what was cleared");
  return {
    ok: true,
    value: {
      theme,
      cleared: { clientDefaults: counts[0] ?? 0, showThemes: counts[1] ?? 0, surfacePointers: counts[2] ?? 0 },
    },
  };
}

export async function readClientDefault(
  options: ApiOptions,
  token: SessionToken,
  clientId: string,
): Promise<ThemeAttempt<ClientDefault>> {
  const answer = await authenticated(options, token, {
    path: `${PEOPLE_ENDPOINTS.clientThemes}?client_id=${encodeURIComponent(clientId)}`,
    method: "GET",
  });
  if (!answer.ok) return refused(answer);
  return clientDefaultFrom(answer.body);
}

export async function saveClientDefault(
  options: ApiOptions,
  token: SessionToken,
  input: { readonly clientId: string; readonly themeId: string | null },
): Promise<ThemeAttempt<ClientDefault>> {
  const answer = await authenticated(options, token, {
    path: PEOPLE_ENDPOINTS.clientThemes,
    method: "PUT",
    json: { client_id: input.clientId, theme_id: input.themeId },
  });
  if (!answer.ok) return refused(answer);
  return clientDefaultFrom(answer.body);
}

export async function readShowThemes(
  options: ApiOptions,
  token: SessionToken,
  activationId: string,
): Promise<ThemeAttempt<ShowThemes>> {
  const answer = await authenticated(options, token, {
    path: `${PEOPLE_ENDPOINTS.activationThemes}?activation_id=${encodeURIComponent(activationId)}`,
    method: "GET",
  });
  if (!answer.ok) return refused(answer);
  return showThemesFrom(answer.body);
}

/**
 * What a show wears, written whole.
 *
 * The route replaces both fields at once, so this always sends both: the
 * event theme, and every app's pointer. A key left out of `surfaceThemes`
 * is the app following the show, and "own" is the app on its own system.
 * Those are two different answers and the screens keep them apart.
 */
export async function saveShowThemes(
  options: ApiOptions,
  token: SessionToken,
  input: {
    readonly activationId: string;
    readonly showThemeId: string | null;
    readonly surfaceThemes: Readonly<Partial<Record<ThemeSurfaceId, string>>>;
  },
): Promise<ThemeAttempt<ShowThemes>> {
  const answer = await authenticated(options, token, {
    path: PEOPLE_ENDPOINTS.activationThemes,
    method: "PUT",
    json: {
      activation_id: input.activationId,
      show_theme_id: input.showThemeId,
      surface_themes: input.surfaceThemes,
    },
  });
  if (!answer.ok) return refused(answer);
  return showThemesFrom(answer.body);
}

// ------------------------------------------------------------------ reading

function refused(answer: Extract<Answer, { ok: false }>): { readonly ok: false } & (AdminRefusal | BelowFloor) {
  const error = typeof answer.body?.error === "string" ? answer.body.error : null;
  if (answer.status === 422 && error === "theme_below_contrast_floor") {
    const said = typeof answer.body?.detail === "string" ? answer.body.detail : "";
    return {
      ok: false,
      kind: "below_floor",
      detail: said === "" ? "The service measured a pair below the contrast floor. Save is refused until they pass." : said,
    };
  }
  return { ok: false, ...adminRefusalFor(answer) };
}

function wire(input: ThemeDraft): Record<string, unknown> {
  return {
    name: input.name,
    light: input.light,
    dark: input.dark,
    head_font: input.headFont,
    body_font: input.bodyFont,
    radius: input.radius,
    logos: input.logos,
  };
}

function recordFrom(value: unknown): LibraryRecord | null {
  const b = fields(value);
  const themeId = text(b["theme_id"]);
  const name = text(b["name"]);
  const light = paletteFrom(b["light"]);
  const dark = paletteFrom(b["dark"]);
  const headFont = text(b["head_font"]);
  const bodyFont = text(b["body_font"]);
  const radius = b["radius"] === "pill" || b["radius"] === "square" ? b["radius"] : null;
  if (themeId === null || name === null || light === null || dark === null) return null;
  if (headFont === null || bodyFont === null || radius === null) return null;
  const logos = b["logos"];
  return {
    themeId,
    name,
    clientId: text(b["client_id"]),
    locked: b["locked"] === true,
    light,
    dark,
    headFont,
    bodyFont,
    radius,
    logos: typeof logos === "object" && logos !== null && !Array.isArray(logos) ? (logos as Record<string, unknown>) : null,
    revisedAt: text(b["revised_at"]),
    archivedAt: text(b["archived_at"]),
  };
}

/** The twelve roles as strings, or nothing. Whether each is a colour is the audit's question. */
function paletteFrom(value: unknown): ThemePalette | null {
  const b = fields(value);
  const out: Record<string, string> = {};
  for (const key of THEME_ROLE_KEYS) {
    const v = b[key];
    if (typeof v !== "string") return null;
    out[key] = v;
  }
  return out as ThemePalette;
}

function clientDefaultFrom(body: unknown): ThemeAttempt<ClientDefault> {
  const b = fields(body);
  const clientId = text(b["client_id"]);
  if (clientId === null) return unreadable("client default", "client_id");
  return { ok: true, value: { clientId, themeId: text(b["theme_id"]) } };
}

const FOLLOWS: readonly ThemeFollows[] = ["surface", "own", "show", "client", "nothing"];

function showThemesFrom(body: unknown): ThemeAttempt<ShowThemes> {
  const b = fields(body);
  const activationId = text(b["activation_id"]);
  const clientId = text(b["client_id"]);
  const rawSurfaces = b["surfaces"];
  if (activationId === null || clientId === null || !Array.isArray(rawSurfaces)) {
    return unreadable("show's themes", "activation_id, client_id and surfaces");
  }
  const surfaceThemes: Partial<Record<ThemeSurfaceId, string>> = {};
  for (const [key, pointer] of Object.entries(fields(b["surface_themes"]))) {
    if (typeof pointer === "string") surfaceThemes[key as ThemeSurfaceId] = pointer;
  }
  const surfaces: ShowThemes["surfaces"][number][] = [];
  for (const entry of rawSurfaces) {
    const row = fields(entry);
    const surface = text(row["surface"]);
    const follows = FOLLOWS.find((f) => f === row["follows"]);
    if (surface === null || follows === undefined) return unreadable("show's themes", "each app's surface and follows");
    surfaces.push({ surface: surface as ThemeSurfaceId, themeId: text(row["theme_id"]), follows });
  }
  return {
    ok: true,
    value: {
      activationId,
      clientId,
      showThemeId: text(b["show_theme_id"]),
      surfaceThemes,
      clientDefaultThemeId: text(b["client_default_theme_id"]),
      surfaces,
    },
  };
}

/** admin.ts's sentence for a 200 it could not read, refused rather than half-drawn. */
function unreadable(what: string, wanted: string): { readonly ok: false } & AdminRefusal {
  return {
    ok: false,
    kind: "unreachable",
    detail:
      `The service answered with something this console cannot read as the ${what}: ${wanted} are what it ` +
      `needs. If that was a save it may well have worked, so read the page again before repeating it.`,
  };
}

const fields = (body: unknown): Record<string, unknown> =>
  typeof body === "object" && body !== null && !Array.isArray(body) ? (body as Record<string, unknown>) : {};

const text = (value: unknown): string | null => (typeof value === "string" && value !== "" ? value : null);

const count = (value: unknown): number | null =>
  typeof value === "number" && Number.isInteger(value) && value >= 0 ? value : null;
